/**
 * ONE TYPED MAKE STRING, ONE CANONICAL MAKE.
 *
 * The make on a vehicle record is typed by a person standing next to a car,
 * and everything downstream keys off it -- which factory portal to open,
 * which archive has the manual, what the assistant is told about the car.
 *
 * Measured against the live book on 2026-09-19 -- 839 jobs with a vehicle --
 * roughly one in twenty of those strings does not resolve on its own:
 *
 *   CHEVY            17 jobs, sitting apart from CHEVROLET's 79
 *   SILVERADO 2, CAMARO 2, CORVETTE 1, TAHOE 1, SAFARI 1   model in the make box
 *   BENZ 3, MERCEDES BENZ 1                 apart from MERCEDES-BENZ's 8
 *   VW 2, HUNDAI 1, INFINITE 1              abbreviation and typo
 *   CASCADIA 1, ORION 2                     not a car at all
 *   "?" 4
 *
 * Seventeen Chevrolets that miss the portal table is not a rounding error,
 * and a technician sent to the wrong manufacturer once stops pressing the
 * button for good.
 *
 * WHAT THIS FILE IS NOT
 *
 * It does not know which website to open. manuals.html already carries
 * OEM_SERVICE, a researched table of factory portals that correctly treats
 * Kia and Hyundai, Nissan and Infiniti, VW and Audi as the separate
 * subscriptions they are. This module resolves the string; that table owns
 * the routing. The first draft of this file carried its own group map, and
 * the two would have drifted within a month -- one table, one owner.
 *
 * So the join is: raw string -> normalizeMake() -> canonical make ->
 * OEM_SERVICE[make.toUpperCase()] -> portal. Every canonical make below is
 * spelled to match that table's keys.
 *
 * THE VIN IS THE REAL ANSWER AND THIS IS THE FALLBACK. A VIN names the
 * manufacturer outright and NHTSA decodes it free; openTools() already
 * prefers it. Never let a string match quietly outrank a decode.
 *
 * TWO RULES THAT MATTER MORE THAN THE COVERAGE:
 *
 *   1. A GUESS MUST ANNOUNCE ITSELF. Reading SILVERADO as a Chevrolet is
 *      almost certainly right and still a guess. Anything inferred from a
 *      model name comes back ask:true, and the caller confirms before it
 *      sends anybody to a checkout. Silent guessing buys the wrong pass.
 *
 *   2. A TRUCK IS REFUSED, NOT ROUTED. canBroker() returns false for heavy
 *      duty whatever the portal table says, so a Cascadia can never be sold
 *      a light-duty car pass by accident.
 *
 * Extend the tables from real strings that turned up in the book, not from
 * imagination. An alias nobody has ever typed is a line to maintain forever.
 */

/**
 * Every make this module will name, spelled as OEM_SERVICE keys it.
 *
 * Membership is not a claim that a portal exists -- that is the portal
 * table's business, and it is allowed to have no row for a make.
 */
const CANONICAL_MAKES = [
  "Buick", "Cadillac", "Chevrolet", "GMC", "Hummer", "Oldsmobile", "Pontiac",
  "Saturn", "Geo", "General Motors",
  "Ford", "Lincoln", "Mercury",
  "Chrysler", "Dodge", "Jeep", "Ram", "Fiat", "Alfa Romeo", "Plymouth",
  "SRT", "Eagle", "Maserati",
  "Toyota", "Lexus", "Scion",
  "Honda", "Acura",
  "Nissan", "Infiniti", "Datsun",
  "Hyundai", "Kia", "Genesis",
  "Subaru", "Mazda", "Mitsubishi", "Suzuki", "Isuzu",
  "BMW", "Mini",
  "Mercedes-Benz", "Smart",
  "Volkswagen", "Audi", "Porsche",
  "Volvo", "Jaguar", "Land Rover",
  "Tesla", "Rivian",
  // Heavy duty. Named so the answer is honest, never so a car pass is sold
  // against them -- see HEAVY_DUTY and canBroker below.
  "Freightliner", "International", "Kenworth", "Peterbilt", "Mack",
  "Western Star", "Hino", "UD",
];

/**
 * What people actually type -> the canonical make.
 *
 * Keys are compared after upper-casing and stripping every character that
 * is not a letter or a digit, so one entry covers "Mercedes-Benz",
 * "mercedes benz", "MERCEDES_BENZ" and "MercedesBenz" at once.
 */
const ALIASES = {
  // Seen in the book
  "CHEVY": "Chevrolet", "HUNDAI": "Hyundai", "INFINITE": "Infiniti",
  "VW": "Volkswagen", "BENZ": "Mercedes-Benz", "MERCEDES": "Mercedes-Benz",

  // Near neighbours of those, cheap to carry
  "CHEV": "Chevrolet", "CHEVORLET": "Chevrolet", "CHEVEROLET": "Chevrolet",
  "CHEVROLE": "Chevrolet",
  "VOLKSWAGON": "Volkswagen", "VOLKS": "Volkswagen",
  "MBENZ": "Mercedes-Benz",
  "HYUNDIA": "Hyundai", "HYNDAI": "Hyundai",
  "INFINITY": "Infiniti",
  "LANDROVER": "Land Rover", "RANGEROVER": "Land Rover", "ROVER": "Land Rover",
  "JAG": "Jaguar", "SUBIE": "Subaru", "TOYOTTA": "Toyota",
  "MINICOOPER": "Mini", "ALFA": "Alfa Romeo",
  "DODGERAM": "Ram", "MOPAR": "Ram",

  // The van is badged both ways and OEM_SERVICE has carried a row for each
  // since before this module existed. Routing lookups through here would
  // have orphaned both -- see the reachability test.
  "SPRINTER": "Mercedes-Benz", "FREIGHTLINERSPRINTER": "Mercedes-Benz",

  // A group name typed where a make belongs. It resolves, because one
  // ACDelco subscription covers every GM badge, but it still asks -- the
  // year and the model came from somewhere and the badge should too.
  "GM": "General Motors", "GENERALMOTORS": "General Motors",
};

/**
 * Model names that turn up in the make box.
 *
 * Everything here comes back ask:true. It is a strong inference, not a
 * fact, and the difference costs real money at a checkout.
 */
const MODEL_MAKES = {
  // Seen in the book
  "SILVERADO": "Chevrolet", "CAMARO": "Chevrolet", "CORVETTE": "Chevrolet",
  "TAHOE": "Chevrolet", "SAFARI": "GMC",

  "SUBURBAN": "Chevrolet", "MALIBU": "Chevrolet", "EQUINOX": "Chevrolet",
  "IMPALA": "Chevrolet", "COLORADO": "Chevrolet", "TRAVERSE": "Chevrolet",
  "CRUZE": "Chevrolet", "TRAILBLAZER": "Chevrolet",
  "SIERRA": "GMC", "YUKON": "GMC", "ACADIA": "GMC", "TERRAIN": "GMC",
  "CANYON": "GMC", "DENALI": "GMC",

  "F150": "Ford", "F250": "Ford", "F350": "Ford", "MUSTANG": "Ford",
  "EXPLORER": "Ford", "ESCAPE": "Ford", "FUSION": "Ford", "FOCUS": "Ford",
  "RANGER": "Ford", "BRONCO": "Ford", "EXPEDITION": "Ford", "TAURUS": "Ford",

  "CAMRY": "Toyota", "COROLLA": "Toyota", "TACOMA": "Toyota",
  "TUNDRA": "Toyota", "RAV4": "Toyota", "PRIUS": "Toyota",
  "HIGHLANDER": "Toyota", "SIENNA": "Toyota", "4RUNNER": "Toyota",

  "CIVIC": "Honda", "ACCORD": "Honda", "CRV": "Honda", "PILOT": "Honda",
  "ODYSSEY": "Honda",

  "ALTIMA": "Nissan", "SENTRA": "Nissan", "ROGUE": "Nissan",
  "FRONTIER": "Nissan", "TITAN": "Nissan", "MAXIMA": "Nissan",

  "WRANGLER": "Jeep", "CHEROKEE": "Jeep", "GRANDCHEROKEE": "Jeep",
  "GLADIATOR": "Jeep", "COMPASS": "Jeep", "RENEGADE": "Jeep",
  "CHARGER": "Dodge", "CHALLENGER": "Dodge", "DURANGO": "Dodge",
  "JOURNEY": "Dodge", "CARAVAN": "Dodge", "GRANDCARAVAN": "Dodge",
  "PROMASTER": "Ram",

  "ELANTRA": "Hyundai", "SONATA": "Hyundai", "TUCSON": "Hyundai",
  "SANTAFE": "Hyundai", "ACCENT": "Hyundai",
  "OPTIMA": "Kia", "SORENTO": "Kia", "SPORTAGE": "Kia", "SOUL": "Kia",
  "FORTE": "Kia", "TELLURIDE": "Kia",

  "OUTBACK": "Subaru", "FORESTER": "Subaru", "IMPREZA": "Subaru",
  "CROSSTREK": "Subaru", "LEGACY": "Subaru", "WRX": "Subaru",
};

/**
 * Not a light-duty car.
 *
 * A Cascadia is a Freightliner tractor and an Orion is a transit bus. No OEM
 * car pass covers either, and neither belongs in ProDemand -- they want
 * TruckSeries or the manufacturer's own fleet portal. The page must be able
 * to say "not covered here", which it cannot do unless something tells it.
 * This is that something.
 *
 * ISUZU and VOLVO are deliberately absent: both badges sell cars and trucks,
 * and the make alone cannot tell them apart. Their commercial model names
 * carry the flag instead.
 */
const HEAVY_DUTY = {
  "CASCADIA": "Freightliner", "FREIGHTLINER": "Freightliner",
  "WESTERNSTAR": "Western Star", "STERLING": "Freightliner",
  "KENWORTH": "Kenworth", "PETERBILT": "Peterbilt", "MACK": "Mack",
  "INTERNATIONAL": "International", "NAVISTAR": "International",
  "HINO": "Hino", "UD": "UD",
  "NPR": "Isuzu", "NQR": "Isuzu", "NRR": "Isuzu",
  "ORION": null, "BLUEBIRD": null, "THOMAS": null, "GILLIG": null,
  "NEWFLYER": null,
};

/**
 * Strings that mean "nobody filled this in".
 *
 * Keyed the same way as everything else, so "n/a" arrives as NA and "-",
 * "?" and "   " all reduce to the empty string and never reach here.
 */
const BLANKS = {
  "NA": 1, "NONE": 1, "UNKNOWN": 1, "UNK": 1, "OTHER": 1, "TBD": 1,
};

/** Upper-case, and drop anything that is not a letter or a digit. */
function key(s) {
  return String(s == null ? "" : s).toUpperCase().replace(/[^A-Z0-9]/g, "");
}

const CANONICAL = {};                 // "MERCEDESBENZ" -> "Mercedes-Benz"
for (const m of CANONICAL_MAKES) CANONICAL[key(m)] = m;

/**
 * Resolve one make string.
 *
 * Returns, always -- never null, never a throw:
 *
 *   raw        what came in, untouched
 *   make       canonical make, spelled as OEM_SERVICE keys it, or null
 *   confidence 'exact' | 'alias' | 'group' | 'model' | 'heavy' | 'blank' | 'unknown'
 *   heavyDuty  true when this is a truck or a bus
 *   ask        true when the caller must confirm before spending money
 *
 * `ask` is the field the UI is required to honour. It is set for every
 * answer that was inferred rather than read, and for every non-answer.
 */
function normalizeMake(raw) {
  const k = key(raw);
  const out = {
    raw: raw == null ? "" : String(raw),
    make: null, confidence: "unknown", heavyDuty: false, ask: true,
  };

  if (!k || BLANKS[k]) { out.confidence = "blank"; return out; }

  if (Object.prototype.hasOwnProperty.call(HEAVY_DUTY, k)) {
    out.make = HEAVY_DUTY[k];
    out.confidence = "heavy";
    out.heavyDuty = true;
    return out;                          // ask stays true: never auto-buy
  }

  if (CANONICAL[k]) {
    out.make = CANONICAL[k];
    out.confidence = out.make === "General Motors" ? "group" : "exact";
    out.ask = out.confidence === "group";
    return out;
  }

  if (ALIASES[k]) {
    out.make = ALIASES[k];
    out.confidence = out.make === "General Motors" ? "group" : "alias";
    out.ask = out.confidence === "group";
    return out;
  }

  if (MODEL_MAKES[k]) {
    out.make = MODEL_MAKES[k];
    out.confidence = "model";
    return out;                          // ask stays true: it is a guess
  }

  return out;
}

/**
 * May a pass be sold for this car, at this portal?
 *
 * The portal comes from OEM_SERVICE, because that table owns routing. This
 * function exists so the heavy-duty refusal cannot be forgotten at a call
 * site: a truck is false even if somebody adds a Freightliner row to the
 * portal table tomorrow.
 *
 * It deliberately ignores `ask`. "There is something to buy here" and "we
 * may take somebody to a checkout without asking first" are different
 * questions, and only the caller knows whether a guess has been confirmed.
 */
function canBroker(res, portal) {
  return !!(res && res.make && portal && !res.heavyDuty);
}

export { normalizeMake, canBroker, CANONICAL_MAKES, ALIASES, MODEL_MAKES, HEAVY_DUTY };

/**
 * manuals.html is one long classic <script>, not a module, so it cannot
 * import. Importing this file anywhere on the page publishes the same
 * functions on the global for that inline code to reach:
 *
 *   <script type="module">import '/make-normalize.js';</script>
 *
 * Module scripts are deferred, so the global appears after parsing. Nothing
 * may call it during initial parse -- every caller runs off a vehicle
 * lookup, which is always later. Callers still guard and fall back to the
 * behaviour the page had before this file existed, rather than breaking.
 */
if (typeof globalThis !== "undefined") {
  globalThis.MakeNormalize = {
    normalizeMake, canBroker, CANONICAL_MAKES, ALIASES, MODEL_MAKES, HEAVY_DUTY,
  };
}
